import React from 'react';

import type { FaceId } from '@vlabs/api/artemy/model';
import { PinFilledIcon, ArrowInCircleRightIcon } from '@vlabs/icons';
import { useHasPermission } from '@vlabs/permissions';
import { routes } from '@vlabs/shared/navigation/routes';
import type { FaceSampleProps } from '@vlabs/uikit';
import {
  FaceSample, Tooltip, Control, CircleAnimatedButton,
} from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import st from './BaseFaceCard.module.sass';

export type TFaceCardSizePoint3 = 'large' | 'small';

export interface BaseFaceCardPoint3Props extends
  Pick<FaceSampleProps, 'src'>,
  WithDataTestid {
  size: TFaceCardSizePoint3;
  faceId?: FaceId;
  shouldShowPin?: boolean;
}

export function BaseFaceCardPoint3({
  size,
  src,
  faceId = undefined,
  shouldShowPin = false,
}: BaseFaceCardPoint3Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const canFaceView = useHasPermission('card', 'read');

  const onViewFace = () => {
    history.push(generatePath(routes.facePage, { faceId }));
  };

  if (!faceId) return null;

  return (
    <React.Fragment>
      {!!shouldShowPin && (
        <div
          className={st.MainFace}
          data-testid="BaseFaceCard.MainFace"
        >
          <Tooltip
            position="bottom"
            tooltipContent={t('monokit:face card.face.main face')}
          >
            <Control.IconButton
              className={st.MainFaceIcon}
              icon={<PinFilledIcon />}
              size="small"
              variant="dimmed"
            />
          </Tooltip>
        </div>
      )}

      <FaceSample
        buttonOnTop={canFaceView
          ? (
            <CircleAnimatedButton
              data-testid="faceCard.goToButton"
              icon={<ArrowInCircleRightIcon />}
              onClick={onViewFace}
              size="small"
            >
              {t('monokit:face card.buttons.face details')}
            </CircleAnimatedButton>
          ) : undefined}
        className={cn(
          st.BaseFaceCardImage,
          st[`BaseFaceCardImage__${size}`],
        )}
        data-testid="BaseFaceCard.Sample"
        size={size}
        src={src}
      />
    </React.Fragment>
  );
}
